/**
 * Phone number entry split in two parts — "Prefix" (default +34) and
 * "Number" — composing a single E.164-ish string ("+34612345678") for the
 * form value, which is what the backend expects.
 *
 * Two flavors:
 *  - <PhoneNumberField>  controlled (value/onChange) — react-hook-form.
 *  - <PhoneNumberInput>  react-admin form input (source) — RA forms.
 *
 * The two parts are INTERNAL state: the composed value is never re-parsed
 * after our own onChange (a composed "+346…" is ambiguous — the greedy
 * prefix would swallow the number's first digits). Re-parsing only happens
 * when the value changes externally (form reset, record load), using a
 * known-prefix list with a 2-digit fallback.
 *
 * An empty number yields an empty composed value (the bare prefix never
 * counts as "filled"), so required-validation keeps working unchanged.
 */

import React, { useEffect, useRef, useState } from "react";
import { Box, TextField, type SxProps, type Theme } from "@mui/material";
import { useInput, useTranslate, type Validator } from "react-admin";

const DEFAULT_PREFIX = "+34";

// Longest-match-first country codes we expect to see; anything else falls
// back to "+ 2 digits". Only used when parsing EXTERNAL values.
const KNOWN_PREFIXES = [
  "+351", // PT
  "+212", // MA
  "+34", // ES
  "+33", // FR
  "+39", // IT
  "+44", // UK
  "+49", // DE
  "+41", // CH
  "+31", // NL
  "+32", // BE
  "+43", // AT
  "+52", // MX
  "+54", // AR
  "+55", // BR
  "+57", // CO
  "+56", // CL
  "+1", // US/CA
];

interface PhoneParts {
  prefix: string;
  number: string;
}

const splitPhone = (value: string): PhoneParts => {
  const v = (value ?? "").trim();
  if (!v) return { prefix: DEFAULT_PREFIX, number: "" };
  if (v.startsWith("+")) {
    const known = KNOWN_PREFIXES.find((p) => v.startsWith(p));
    if (known) return { prefix: known, number: v.slice(known.length).trim() };
    const m = /^(\+\d{1,2})(.*)$/.exec(v);
    if (m) return { prefix: m[1], number: m[2].trim() };
  }
  return { prefix: DEFAULT_PREFIX, number: v };
};

const sanitizePrefix = (raw: string): string => {
  const digits = raw.replace(/[^\d]/g, "").slice(0, 3);
  return `+${digits}`;
};

const compose = (prefix: string, number: string): string => {
  const n = number.trim();
  return n ? `${prefix}${n.replace(/\s+/g, "")}` : "";
};

export interface PhoneNumberFieldProps {
  value: string;
  onChange: (value: string) => void;
  onBlur?: () => void;
  label?: string;
  required?: boolean;
  error?: boolean;
  helperText?: string;
  disabled?: boolean;
  sx?: SxProps<Theme>;
  /** Optional adornment (e.g. a help icon) shown at the end of the number field. */
  endAdornment?: React.ReactNode;
}

export const PhoneNumberField: React.FC<PhoneNumberFieldProps> = ({
  value,
  onChange,
  onBlur,
  label,
  required = false,
  error = false,
  helperText,
  disabled = false,
  sx,
  endAdornment,
}) => {
  const translate = useTranslate();
  const [parts, setParts] = useState<PhoneParts>(() => splitPhone(value));
  // Last value WE emitted — anything else arriving via props is an
  // external change (reset, record load) and triggers a re-parse.
  const lastComposedRef = useRef<string>(compose(parts.prefix, parts.number));

  useEffect(() => {
    const incoming = (value ?? "").trim();
    if (incoming !== lastComposedRef.current) {
      const next = splitPhone(incoming);
      setParts(next);
      lastComposedRef.current = incoming;
    }
  }, [value]);

  const update = (next: PhoneParts): void => {
    setParts(next);
    const composed = compose(next.prefix, next.number);
    lastComposedRef.current = composed;
    onChange(composed);
  };

  return (
    <Box sx={{ display: "flex", gap: 1, width: "100%", ...sx }}>
      <TextField
        label={translate("common.phone.prefix", { _: "Prefix" })}
        value={parts.prefix}
        onChange={(e) =>
          update({ ...parts, prefix: sanitizePrefix(e.target.value) })
        }
        onBlur={onBlur}
        disabled={disabled}
        // Fits "+999" (4 mono chars) and nothing more.
        sx={{ width: 64, flexShrink: 0 }}
        inputProps={{ className: "rg-mono", inputMode: "tel" }}
      />
      <TextField
        label={label ?? translate("common.phone.number", { _: "Number" })}
        value={parts.number}
        onChange={(e) =>
          update({
            ...parts,
            number: e.target.value.replace(/[^\d\s]/g, ""),
          })
        }
        onBlur={onBlur}
        required={required}
        error={error}
        helperText={helperText}
        disabled={disabled}
        fullWidth
        // Room for a full 9-digit national number without truncation.
        sx={{ minWidth: "calc(9ch + 30px)" }}
        inputProps={{ className: "rg-mono", inputMode: "tel" }}
        InputProps={endAdornment ? { endAdornment } : undefined}
      />
    </Box>
  );
};

export interface PhoneNumberInputProps {
  source: string;
  label?: string;
  validate?: Validator | Validator[];
  required?: boolean;
  sx?: SxProps<Theme>;
  /** Optional adornment (e.g. a help icon) shown at the end of the number field. */
  endAdornment?: React.ReactNode;
}

/** react-admin flavor — drop-in for <TextInput source=…> phone fields. */
export const PhoneNumberInput: React.FC<PhoneNumberInputProps> = ({
  source,
  label,
  validate,
  required = false,
  sx,
  endAdornment,
}) => {
  const { field, fieldState } = useInput({ source, validate });
  return (
    <PhoneNumberField
      value={(field.value as string) ?? ""}
      onChange={field.onChange}
      onBlur={field.onBlur}
      label={label}
      required={required}
      error={Boolean(fieldState.error)}
      helperText={fieldState.error?.message}
      sx={sx}
      endAdornment={endAdornment}
    />
  );
};

export default PhoneNumberField;
